import { useDndContext } from '@dnd-kit/core';
import { VerticalDragState } from '../../hooks/useVerticalDrag';

interface SnapLineProps {
  verticalDragState: VerticalDragState | null;
  laneHeight?: number;
  laneGap?: number;
}

const DEFAULTS = {
  LANE_HEIGHT: 30,
  LANE_GAP: 4
};

const getSnapLinePosition = (
  laneIndex: number,
  snapPosition: 'top' | 'bottom' | null,
  laneHeight: number,
  laneGap: number
): number | null => {
  if (!snapPosition) {
    return null;
  }

  const totalLaneHeight = laneHeight + laneGap;
  const laneTop = laneIndex * totalLaneHeight;

  if (snapPosition === 'top') {
    // Place the line in the gap above the target lane (bottom edge of the lane
    // above), so it sits visually between the two tracks rather than inside one.
    return Math.max(0, laneTop - laneGap);
  }
  return laneTop + laneHeight;
};

export const SnapLine = ({
  verticalDragState,
  laneHeight = DEFAULTS.LANE_HEIGHT,
  laneGap = DEFAULTS.LANE_GAP
}: SnapLineProps) => {
  const { active } = useDndContext();

  // Hide immediately when no drag is active. React state cleanup (verticalDragState → null)
  // may land one render later than dnd-kit clearing `active`, so this prevents a brief flicker.
  if (!active) {
    return null;
  }

  if (
    verticalDragState?.targetLaneIndex === null ||
    verticalDragState?.targetLaneIndex === undefined ||
    !verticalDragState?.snapPosition
  ) {
    return null;
  }

  const snapLineY = getSnapLinePosition(
    verticalDragState.targetLaneIndex,
    verticalDragState.snapPosition,
    laneHeight,
    laneGap
  );

  if (snapLineY === null) {
    return null;
  }

  return (
    <div
      data-testid="snapline"
      className="pointer-events-none absolute inset-x-0 z-50 h-0.5 bg-black transition-opacity duration-150"
      style={{ top: `${snapLineY}px` }}
    />
  );
};
